import { z } from 'zod';
import { gstinSchema } from '../money/gst.js';
import { indianMobileSchema, stateCodeSchema } from '../schemas/common.js';
import {
  CUSTOMER_NAME_MAX_LENGTH,
  CUSTOMER_NOTES_MAX_LENGTH,
  CUSTOMER_SOURCES,
  MAX_TAGS_PER_CUSTOMER,
  type CreateCustomerInput,
  type CustomerSource,
} from '../schemas/customers.js';
import type { DelimitedRow } from './delimited.js';

/**
 * What a column in somebody else's spreadsheet MEANS.
 *
 * The rule this module exists to keep is that the shop maps its columns to our fields, never
 * the other way round. Demanding a fixed layout — "your file must have these headings, in this
 * order" — moves the work onto the person with the least reason to do it and the most reason
 * to give up, and every shop's list is laid out differently because every shop's list was
 * built by hand over years.
 *
 * So: headings are GUESSED here (well enough that most files need no correction at all), the
 * guess is always shown and always editable, and nothing is written until the owner has seen a
 * preview of what each column became.
 *
 * Both runtimes read this file. The browser uses it to draw the mapping screen and to preview
 * what a row would produce; the API uses the same functions to validate every row again before
 * writing it, because a preview the client computed is a suggestion, not a permission.
 */

// --- The fields a column can be mapped onto -------------------------------------------------

export const CUSTOMER_IMPORT_FIELDS = [
  'name',
  'primaryPhone',
  'email',
  'addressLine1',
  'addressLine2',
  'city',
  'stateCode',
  'pincode',
  'notes',
  'tags',
  'source',
  'gstin',
] as const;
export type CustomerImportField = (typeof CUSTOMER_IMPORT_FIELDS)[number];
export const customerImportFieldSchema = z.enum(CUSTOMER_IMPORT_FIELDS);

export interface CustomerImportFieldDefinition {
  readonly key: CustomerImportField;
  /** English label; the UI resolves `import.field.<key>` through i18next. */
  readonly defaultLabel: string;
  /** A row without one of these cannot be written at all. */
  readonly required: boolean;
  /**
   * Headings this field answers to, compared after case, punctuation and spacing are folded
   * away. They are deliberately generous — "mobile", "phone no", "contact number" and "cell"
   * are the same column, and a guess that is right nine times out of ten is the difference
   * between a mapping screen somebody confirms and one they have to fill in.
   */
  readonly aliases: readonly string[];
}

export const CUSTOMER_IMPORT_FIELD_DEFINITIONS: readonly CustomerImportFieldDefinition[] =
  Object.freeze([
    {
      key: 'name',
      defaultLabel: 'Customer name',
      required: true,
      aliases: ['name', 'customer', 'customername', 'fullname', 'client', 'clientname', 'party'],
    },
    {
      key: 'primaryPhone',
      defaultLabel: 'Mobile number',
      required: true,
      aliases: [
        'phone',
        'phoneno',
        'phonenumber',
        'mobile',
        'mobileno',
        'mobilenumber',
        'contact',
        'contactno',
        'contactnumber',
        'cell',
        'whatsapp',
        'whatsappnumber',
      ],
    },
    {
      key: 'email',
      defaultLabel: 'Email',
      required: false,
      aliases: ['email', 'emailaddress', 'mail', 'mailid', 'emailid'],
    },
    {
      key: 'addressLine1',
      defaultLabel: 'Address line 1',
      required: false,
      aliases: ['address', 'addressline1', 'address1', 'street', 'line1'],
    },
    {
      key: 'addressLine2',
      defaultLabel: 'Address line 2',
      required: false,
      aliases: ['addressline2', 'address2', 'area', 'landmark', 'line2'],
    },
    {
      key: 'city',
      defaultLabel: 'City',
      required: false,
      aliases: ['city', 'town', 'village', 'district'],
    },
    {
      key: 'stateCode',
      defaultLabel: 'State code',
      required: false,
      aliases: ['statecode', 'gststatecode', 'state'],
    },
    {
      key: 'pincode',
      defaultLabel: 'PIN code',
      required: false,
      aliases: ['pincode', 'pin', 'postcode', 'postalcode', 'zip', 'zipcode'],
    },
    {
      key: 'notes',
      defaultLabel: 'Notes',
      required: false,
      aliases: ['notes', 'note', 'remarks', 'remark', 'comment', 'comments', 'description'],
    },
    {
      key: 'tags',
      defaultLabel: 'Tags',
      required: false,
      aliases: ['tags', 'tag', 'labels', 'category', 'categories', 'group'],
    },
    {
      key: 'source',
      defaultLabel: 'How they found us',
      required: false,
      aliases: ['source', 'referredby', 'referral', 'howtheyfoundus', 'channel', 'leadsource'],
    },
    {
      key: 'gstin',
      defaultLabel: 'GSTIN',
      required: false,
      aliases: ['gstin', 'gstno', 'gstnumber', 'gst', 'gstinno', 'taxid', 'tin'],
    },
  ]);

export const REQUIRED_CUSTOMER_IMPORT_FIELDS: readonly CustomerImportField[] = Object.freeze(
  CUSTOMER_IMPORT_FIELD_DEFINITIONS.filter((field) => field.required).map((field) => field.key),
);

// --- What one column becomes ----------------------------------------------------------------

/**
 * A column's destination. `measurement` is what makes "import measurements alongside
 * customers" fall out of the same screen: a spreadsheet that carries a Bust and a Waist column
 * is a spreadsheet of customers AND their measurements, and asking the owner to import the
 * same file twice through two different screens would be a strange thing to ask.
 */
export const importTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('ignore') }),
  z.object({ kind: z.literal('field'), field: customerImportFieldSchema }),
  z.object({ kind: z.literal('measurement'), fieldKey: z.string().min(1).max(60) }),
]);
export type ImportTarget = z.infer<typeof importTargetSchema>;

export const IGNORED_COLUMN: ImportTarget = Object.freeze({ kind: 'ignore' });

/** A measurement field the shop's chosen garment template offers, for the guesser. */
export interface MeasurementColumnCandidate {
  readonly fieldKey: string;
  readonly label: string;
}

/** Fold a heading down to something comparable: "Phone No." and "phone_no" are one heading. */
export function normaliseHeading(heading: string): string {
  return heading
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

/**
 * The mapping this file most likely wants, one target per column.
 *
 * A field is claimed by the FIRST column that matches it, so a sheet with both "Phone" and
 * "Alt Phone" does not map both onto the primary number. Anything unrecognised is left
 * ignored rather than guessed at: a wrong guess that the owner does not notice writes rubbish
 * into a customer record, while an unmapped column is visibly unfinished.
 */
export function suggestMapping(
  header: readonly string[],
  measurementFields: readonly MeasurementColumnCandidate[] = [],
): ImportTarget[] {
  const claimed = new Set<string>();
  return header.map((heading) => {
    const normalised = normaliseHeading(heading);
    if (normalised.length === 0) return IGNORED_COLUMN;

    for (const definition of CUSTOMER_IMPORT_FIELD_DEFINITIONS) {
      if (claimed.has(definition.key)) continue;
      if (!definition.aliases.includes(normalised)) continue;
      claimed.add(definition.key);
      return { kind: 'field', field: definition.key };
    }

    for (const candidate of measurementFields) {
      const key = `measurement:${candidate.fieldKey}`;
      if (claimed.has(key)) continue;
      if (
        normaliseHeading(candidate.label) !== normalised &&
        normaliseHeading(candidate.fieldKey) !== normalised
      ) {
        continue;
      }
      claimed.add(key);
      return { kind: 'measurement', fieldKey: candidate.fieldKey };
    }

    return IGNORED_COLUMN;
  });
}

// --- Reading one row through the mapping ----------------------------------------------------

export interface MappedMeasurement {
  readonly fieldKey: string;
  /** The cell exactly as typed — `36 1/2` stays `36 1/2` until the API canonicalises it. */
  readonly enteredAs: string;
}

export interface MappedRow {
  readonly rowNumber: number;
  readonly values: Partial<Record<CustomerImportField, string>>;
  readonly measurements: readonly MappedMeasurement[];
}

/** Pull the mapped cells out of one row. Blank cells are left out entirely. */
export function mapRow(row: DelimitedRow, mapping: readonly ImportTarget[]): MappedRow {
  const values: Partial<Record<CustomerImportField, string>> = {};
  const measurements: MappedMeasurement[] = [];

  mapping.forEach((target, column) => {
    const cell = (row.cells[column] ?? '').trim();
    if (cell.length === 0 || target.kind === 'ignore') return;
    if (target.kind === 'field') values[target.field] = cell;
    else measurements.push({ fieldKey: target.fieldKey, enteredAs: cell });
  });

  return { rowNumber: row.rowNumber, values, measurements };
}

// --- Validating one row ---------------------------------------------------------------------

export const IMPORT_ISSUE_CODES = [
  'missing_required',
  'invalid_phone',
  'invalid_email',
  'invalid_pincode',
  'invalid_state_code',
  'unknown_source',
  'invalid_gstin',
  'truncated',
  'too_many_tags',
  // The shop's tag list was full, so a tag new to it could not be created.
  'tag_limit_reached',
  'duplicate_in_file',
  'duplicate_in_shop',
] as const;
export type ImportIssueCode = (typeof IMPORT_ISSUE_CODES)[number];

/**
 * One thing wrong with a row.
 *
 * The severity is the whole design. An `error` means the row cannot be written — there is no
 * name, or the number is not a number. A `warning` means something was dropped or trimmed and
 * the rest of the row is still worth having: an unreadable "how they found us" value must not
 * cost a shop the customer's phone number, which is the only part of the record that matters.
 */
export interface ImportIssue {
  readonly field: CustomerImportField | null;
  readonly code: ImportIssueCode;
  readonly severity: 'error' | 'warning';
  readonly message: string;
}

export interface ValidatedRow {
  readonly rowNumber: number;
  /** The canonical ten-digit number this row is identified by, when it has a valid one. */
  readonly phone: string | null;
  /** Ready to create, or `null` when an error means the row cannot be written. */
  readonly customer: CreateCustomerInput | null;
  readonly measurements: readonly MappedMeasurement[];
  readonly issues: readonly ImportIssue[];
}

const SOURCE_ALIASES: Readonly<Record<string, CustomerSource>> = Object.freeze({
  walkin: 'walk_in',
  walkinclient: 'walk_in',
  shop: 'walk_in',
  counter: 'walk_in',
  wordofmouth: 'referral',
  friend: 'referral',
  reference: 'referral',
  referred: 'referral',
  existing: 'repeat',
  repeatcustomer: 'repeat',
  insta: 'instagram',
  ig: 'instagram',
  fb: 'facebook',
  wa: 'whatsapp',
  onlinesearch: 'google',
  web: 'google',
  fair: 'exhibition',
  stall: 'exhibition',
});

/** Match a free-text "how they found us" value onto the closed list, or `null`. */
export function matchCustomerSource(raw: string): CustomerSource | null {
  const normalised = normaliseHeading(raw);
  if (normalised.length === 0) return null;
  const direct = CUSTOMER_SOURCES.find((source) => normaliseHeading(source) === normalised);
  if (direct) return direct;
  return SOURCE_ALIASES[normalised] ?? null;
}

/** Tags in a single cell, however the shop separated them: `bridal; regular, VIP`. */
export function splitTags(raw: string): string[] {
  const seen = new Map<string, string>();
  for (const part of raw.split(/[;,|/]+/)) {
    const tag = part.trim().replace(/\s+/g, ' ');
    if (!tag) continue;
    const key = tag.toLowerCase();
    if (!seen.has(key)) seen.set(key, tag);
  }
  return [...seen.values()];
}

/**
 * Turn one mapped row into something that could be created, or into the reasons it cannot.
 *
 * Duplicates are NOT decided here: whether this number already belongs to somebody is a
 * question about the shop's book and about the rest of the file, and both of those belong to
 * the caller. What this answers is the part that is true of the row on its own.
 */
export function validateRow(mapped: MappedRow): ValidatedRow {
  const issues: ImportIssue[] = [];
  const error = (
    field: CustomerImportField | null,
    code: ImportIssueCode,
    message: string,
  ): void => {
    issues.push({ field, code, severity: 'error', message });
  };
  const warn = (
    field: CustomerImportField | null,
    code: ImportIssueCode,
    message: string,
  ): void => {
    issues.push({ field, code, severity: 'warning', message });
  };

  const raw = mapped.values;

  let name = (raw.name ?? '').replace(/\s+/g, ' ').trim();
  if (name.length === 0) error('name', 'missing_required', 'This row has no customer name');
  if (name.length > CUSTOMER_NAME_MAX_LENGTH) {
    name = name.slice(0, CUSTOMER_NAME_MAX_LENGTH);
    warn('name', 'truncated', `The name was shortened to ${CUSTOMER_NAME_MAX_LENGTH} characters`);
  }

  const rawPhone = (raw.primaryPhone ?? '').trim();
  let phone: string | null = null;
  if (rawPhone.length === 0) {
    error('primaryPhone', 'missing_required', 'This row has no mobile number');
  } else {
    const parsed = indianMobileSchema.safeParse(rawPhone);
    if (parsed.success) phone = parsed.data;
    else error('primaryPhone', 'invalid_phone', `“${rawPhone}” is not a 10-digit mobile number`);
  }

  let email: string | null = null;
  if (raw.email) {
    const parsed = z.string().trim().email().max(200).safeParse(raw.email);
    if (parsed.success) email = parsed.data;
    else warn('email', 'invalid_email', `“${raw.email}” is not an email address and was left out`);
  }

  let pincode: string | null = null;
  if (raw.pincode) {
    const digits = raw.pincode.replace(/\s+/g, '');
    if (/^\d{6}$/.test(digits)) pincode = digits;
    else warn('pincode', 'invalid_pincode', `“${raw.pincode}” is not a 6-digit PIN code`);
  }

  let stateCode: string | null = null;
  if (raw.stateCode) {
    const digits = raw.stateCode.trim().padStart(2, '0');
    const parsed = stateCodeSchema.safeParse(digits);
    if (parsed.success) stateCode = parsed.data;
    else warn('stateCode', 'invalid_state_code', `“${raw.stateCode}” is not a 2-digit state code`);
  }

  let source: CustomerSource | null = null;
  if (raw.source) {
    source = matchCustomerSource(raw.source);
    if (!source) {
      warn(
        'source',
        'unknown_source',
        `“${raw.source}” is not one of the sources this product groups by, so it was left out`,
      );
    }
  }

  // A GSTIN in the sheet is what says "this row is a business" — nobody maintains a separate
  // Type column, and a wholesale customer list is a list of GSTINs. An unreadable one is a
  // warning rather than an error for the usual reason: it must not cost the shop the row.
  let gstin: string | null = null;
  if (raw.gstin) {
    const parsed = gstinSchema.safeParse(raw.gstin);
    if (parsed.success) gstin = parsed.data;
    else warn('gstin', 'invalid_gstin', `“${raw.gstin}” is not a valid GSTIN and was left out`);
  }

  let tags = raw.tags ? splitTags(raw.tags) : [];
  if (tags.length > MAX_TAGS_PER_CUSTOMER) {
    tags = tags.slice(0, MAX_TAGS_PER_CUSTOMER);
    warn('tags', 'too_many_tags', `Only the first ${MAX_TAGS_PER_CUSTOMER} tags were kept`);
  }

  let notes = raw.notes ?? null;
  if (notes && notes.length > CUSTOMER_NOTES_MAX_LENGTH) {
    notes = notes.slice(0, CUSTOMER_NOTES_MAX_LENGTH);
    warn('notes', 'truncated', 'The note was shortened to fit');
  }

  const fatal = issues.some((issue) => issue.severity === 'error');
  if (fatal || !phone) {
    return { rowNumber: mapped.rowNumber, phone, customer: null, measurements: [], issues };
  }

  return {
    rowNumber: mapped.rowNumber,
    phone,
    customer: {
      name,
      primaryPhone: phone,
      kind: gstin ? 'b2b' : 'b2c',
      gstin,
      email,
      address: {
        line1: raw.addressLine1 ?? null,
        line2: raw.addressLine2 ?? null,
        city: raw.city ?? null,
        stateCode,
        pincode,
      },
      notes,
      tags,
      source,
    },
    measurements: mapped.measurements,
    issues,
  };
}

/** True when the mapping covers everything a row must have to be written at all. */
export function mappingIsComplete(mapping: readonly ImportTarget[]): boolean {
  const mapped = new Set(
    mapping.filter((target) => target.kind === 'field').map((target) => target.field),
  );
  return REQUIRED_CUSTOMER_IMPORT_FIELDS.every((field) => mapped.has(field));
}

/** Fields the mapping is still missing — what the "you cannot continue yet" message names. */
export function missingRequiredFields(
  mapping: readonly ImportTarget[],
): readonly CustomerImportField[] {
  const mapped = new Set(
    mapping.filter((target) => target.kind === 'field').map((target) => target.field),
  );
  return REQUIRED_CUSTOMER_IMPORT_FIELDS.filter((field) => !mapped.has(field));
}
